import React from 'react';
import { Field as FormField, Select as FormSelect, SectionHead, SectionDivider, INP } from '../reusable/formRecords/formFieldPrimitives';

function normalizeSubModule(option) {
  if (option === null || option === undefined) return null;

  if (typeof option === 'string') {
    const text = option.trim();
    return text ? { value: text, label: text, description: '' } : null;
  }

  const value = String(option.value ?? option.id ?? option.code ?? '').trim();
  if (!value) return null;

  return {
    value,
    label: String(option.label ?? option.name ?? value).trim() || value,
    description: String(option.description ?? option.hint ?? option.summary ?? '').trim(),
  };
}

export function ModalButtons({ onClose, onBack, onCreate, busy = false, createLabel = 'Create', backLabel = 'Previous', closeLabel = 'Close' }) {
  return (
    <div className="flex gap-3 mt-2 pt-2">
      {onBack ? (
        <button
          onClick={onBack}
          className="flex-1 py-3 rounded-2xl border border-gray-200 text-[13px] font-semibold text-gray-700 hover:bg-gray-50 transition"
        >
          {backLabel}
        </button>
      ) : (
        <button
          onClick={onClose}
          className="flex-1 py-3 rounded-2xl border border-gray-200 text-[13px] font-semibold text-gray-700 hover:bg-gray-50 transition"
        >
          {closeLabel}
        </button>
      )}
      <button
        onClick={onCreate}
        disabled={busy}
        className="flex-1 py-3 rounded-2xl text-[13px] font-semibold text-white bg-green-600 hover:bg-green-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {busy ? 'Creating...' : createLabel}
      </button>
    </div>
  );
}

export function SubModulePanel({
  moduleName,
  moduleDescription = 'Choose a sub module to load the matching form fields.',
  subModuleOptions = [],
  subModule = '',
  onSubModuleChange,
  disabled = false,
  emptyMessage = 'Select a sub module above to continue.',
}) {
  const options = subModuleOptions.map(normalizeSubModule).filter(Boolean);

  return (
    <div className="space-y-4">
      <SectionHead title="Select Module" />
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Module" required>
          <div className="flex items-center min-h-[2.75rem] px-4 py-3 rounded-lg bg-gray-50 text-sm font-semibold text-gray-900">
            {moduleName}
          </div>
        </FormField>

        <FormField label="Sub Module" required>
          <FormSelect
            value={subModule}
            onChange={(event) => onSubModuleChange?.(event.target.value)}
            options={options}
            placeholder="Select Sub Module"
            disabled={disabled}
            className={INP}
          />
        </FormField>
      </div>

      <SectionDivider />
    </div>
  );
}

export default function CoreCreateModal({
  isOpen,
  inline = false,
  onClose,
  children,
}) {
  if (!isOpen && !inline) return null;

  const body = (
    <div className="space-y-5">
      {children ?? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-500">
          Select a sub module to continue.
        </div>
      )}
    </div>
  );

  if (inline) {
    return <div className="w-full">{body}</div>;
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1200] p-4"
    >
      <div
        style={{ animation: 'slideUp 0.2s ease' }}
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <style>{`@keyframes slideUp{from{transform:translateY(24px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 text-xl leading-none transition bg-white/90 shadow-sm"
          aria-label="Close modal"
        >
          &times;
        </button>

        {body}
      </div>
    </div>
  );
}
